export interface IBloodCollectionQueueItem {
  uid: string
  status: (typeof EBloodCollectionStatus)[keyof typeof EBloodCollectionStatus]
  gender: string
  visit_id: string
  fio_initials: string
  birth_date: string
  donation_type_id: string
  visit_type_id: string
  donor_category_id: string
  is_mobile_collection: boolean
  is_fast_tracked: boolean
  queued_at: string
}

export const EBloodCollectionStatus = {
  WAITING: 'WAITING',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
} as const

export type TBloodCollectionStatus =
  (typeof EBloodCollectionStatus)[keyof typeof EBloodCollectionStatus]

export interface IBloodCollectionQueueParams {
  visit_type_id?: string
  status?: (typeof EBloodCollectionStatus)[keyof typeof EBloodCollectionStatus]
  donation_type_id?: string
  donor_category_id?: string
  gender?: string
  is_mobile_collection?: boolean
  search?: string
  page: number
  size: number
}

export interface IBloodCollectionQueueResponse {
  success: boolean
  data: IBloodCollectionQueueItem[]
  errors: {
    code: string
    message: string
    fields: { key: string; value: string }[]
  }[]
  meta: { timestamp: string; request_id: string }
  page: number
  hasNext: boolean
  totalSize: number
  totalPages: number
}

export interface IBloodCollectionItem {
  id: string
  donor_id: string
  fio: string
  birth_date: string
  visit_date: string
  visit_type: string
  visit_type_name: string
  donation_type: string
  donation_type_name: string
  donor_category: string
  donor_category_name: string
  status: string
}

export interface IBloodCollectionParams {
  search?: string
  visit_type?: string
  donation_type_id?: string
  donor_category_id?: string
  status?: (typeof EBloodCollectionStatus)[keyof typeof EBloodCollectionStatus]
  gender?: string
  date_from?: string
  date_to?: string
  page: number
  size: number
}

export interface IBloodCollectionResponse {
  success: boolean
  data: IBloodCollectionItem[]
  errors: {
    code: string
    message: string
    fields: { key: string; value: string }[]
  }[]
  meta: {
    timestamp: string
    request_id: string
    execution_time_ms: number
  }
  page: number
  hasNext: boolean
  totalSize: number
  totalPages: number
}

export interface IBloodCollectionApiError {
  code: string
  message: string
  fields: { key: string; value: string }[]
}

export interface IBloodCollectionApiMeta {
  timestamp: string
  request_id: string
  execution_time_ms: number
}

export interface IBloodCollectionApiResponse<T> {
  success: boolean
  data: T
  errors: IBloodCollectionApiError[]
  meta: IBloodCollectionApiMeta
}

export const EBloodCollectionRejectReason = {
  POOR_WELLBEING: 'POOR_WELLBEING',
  OTHER: 'OTHER',
} as const

export type TBloodCollectionRejectReason =
  (typeof EBloodCollectionRejectReason)[keyof typeof EBloodCollectionRejectReason]

export interface IBloodCollectionRejectPayload {
  reason: TBloodCollectionRejectReason
  comment: string
}

export interface IBloodCollectionStageActionData {
  id: string
  visit_id: string
  person_uuid: string
  status: string
  completed_at: string | null
  completed_by_fio: string | null
  rejected_at: string | null
  rejected_by_fio: string | null
  reject_reason: string | null
  reject_comment: string | null
}

export interface IBloodCollectionAnalysisSummary {
  analysis_code: string
  analysis_name_ru: string
  analysis_name_en: string
}

export interface IBloodCollectionAnalysis {
  id: string
  analysis_template_id: string
  analysis_code: string
  analysis_name_ru: string
  laboratory_id: string
  tube_color_id: string
  tube_color_name: string
  source: string
  status: string
}

export interface IBloodCollectionBarcode {
  id: string
  tube_number: number
  tube_type_id: string
  tube_color_id: string
  tube_type_name: string
  tube_color_name: string
  barcode_value: string
  print_status: string
  print_count: number
  analyses: IBloodCollectionAnalysisSummary[]
}

export interface IBloodCollectionGenerateBarcodesPayload {
  selected_analysis_ids: string[]
}

export interface IBloodCollectionBarcodePrintData {
  donor_card_number: string
  donor_full_name: string
  birth_date: string
  gender: string
  collection_datetime: string
  donor_category_name: string
  donation_type_name: string
  tube_color_name: string
  preliminary_blood_group: string
  rh_factor: string
  hemoglobin_value: number
  screening_doctor_fio: string
  nurse_fio: string
  barcode_value: string
  analyses: IBloodCollectionAnalysisSummary[]
}

export interface IBloodCollectionPatchAnalysesPayload {
  selected_analysis_ids: string[]
  removed_analysis_ids: string[]
  manual_analysis_template_ids: string[]
}

export interface IBloodCollectionHistoryItem {
  id: string
  collection_datetime: string
  screening_doctor_fio: string
  nurse_fio: string
  donor_category: string
  donation_type: string
  analyses: string[]
  barcodes: string[]
  status: string
  reject_reason: string
  reject_comment: string
}

export interface IBloodCollectionDetail {
  id: string
  visit_id: string
  person_uuid: string
  status: string
  donor_card_number: string
  donor_full_name: string
  birth_date: string
  gender: string
  donor_category_name: string
  donation_type_name: string
  visit_type: string
  screening_doctor_fio: string
  preliminary_blood_group: string
  rh_factor: string
  hemoglobin_value: number
  analyses: IBloodCollectionAnalysis[]
  barcodes: IBloodCollectionBarcode[]
  history: IBloodCollectionHistoryItem[]
}
